import styled from "@emotion/styled"
import chroma from "chroma-js"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DEFAULT_DECIMAL_PLACES } from "../helpers"
import { Tooltip } from "./Tooltip"

const colorRamp = ["#FF0000", "#E6E939", "#00A35E"] as const

const Base = styled.div<{ brief?: boolean }>`
  padding: ${({ brief }) => (brief ? "0" : "32px 24px")};
  display: grid;
  grid-template:
    "label percent"
    "axis axis"
    "poor good";
`

const Text = styled.div`
  font-family: "Arial";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.02em;
`

const Label = styled(Text)`
  grid-area: label;
  font-weight: 700;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const Percent = styled(Text)`
  grid-area: percent;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  font-weight: 700;
  font-size: 26px;
  line-height: 36px;
`

const Axis = styled.div<{ brief?: boolean }>`
  grid-area: axis;
  margin-top: ${({ brief }) => (brief ? 0 : 67)}px;
  margin-bottom: ${({ brief }) => (brief ? 0 : 16)}px;
`

const ColorBar = styled.div<{ brief?: boolean }>`
  background: linear-gradient(
    270deg,
    ${colorRamp[2]} 0%,
    ${colorRamp[1]} 49.08%,
    ${colorRamp[0]} 103.55%
  );
  box-shadow: 0px 8px 20px rgba(175, 175, 175, 0.4);
  border-radius: ${({ brief }) => (brief ? 0 : "30px")};
  height: ${({ brief }) => (brief ? "4px" : "24px")};
  width: 100%;
`

const Pointer: React.FC<{ brief?: boolean }> = ({ brief = false }) => {
  return (
    <svg
      width={brief ? 12 : 23}
      height={brief ? 6 : 12}
      viewBox="0 0 23 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: brief ? "rotate(180deg)" : undefined,
      }}
    >
      <path
        d="M23 0.313477L11.6863 11.6272L0.372583 0.313475L11.6863 0.313476L23 0.313477Z"
        fill="black"
      />
    </svg>
  )
}

const Good = styled(Text)`
  grid-area: good;
  text-align: right;
`

const Poor = styled(Text)`
  grid-area: poor;
`

const Dot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 100%;
  margin-right: 8px;
  background: ${({ color }) => color};
`

export const Indicator: React.FC<{ percent: number; brief?: boolean }> = ({
  percent,
  brief = false,
}) => {
  const { t } = useTranslation()
  const axisRef = useRef<HTMLDivElement | null>(null)
  const [axisWidth, setAxisWidth] = useState(0)

  const checkedPercent = Math.min(Math.max(percent, 0), 100)
  const colorScale = useMemo(() => chroma.scale(Array.from(colorRamp)), [])
  const offset = (axisWidth / 100) * checkedPercent
  const color = colorScale(checkedPercent / 100).hex()
  const absoluteOffset = brief ? 10 : 23

  useEffect(() => {
    if (!axisRef.current) return
    setAxisWidth(axisRef.current.clientWidth - absoluteOffset)
  }, [axisRef])

  if (brief)
    return (
      <Tooltip
        content={
          <span>
            {t("general.yourIndex")}:{" "}
            <strong>{checkedPercent.toFixed(DEFAULT_DECIMAL_PLACES)}%</strong>
          </span>
        }
      >
        <Base brief>
          <Axis ref={axisRef} brief>
            <ColorBar brief />
            <div>
              <div
                style={{
                  marginRight: 12,
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <div style={{ width: `${checkedPercent}%` }}></div>
                <div
                  style={{
                    width: 0,
                    overflow: "visible",
                  }}
                >
                  <Pointer brief />
                </div>
              </div>
            </div>
          </Axis>
        </Base>
      </Tooltip>
    )

  return (
    <Base>
      <Label>{t("summary.yourIndicator")}</Label>
      <Percent>
        <Dot color={color} /> {checkedPercent.toFixed(DEFAULT_DECIMAL_PLACES)}%
      </Percent>
      <Axis ref={axisRef}>
        <div>
          <div
            style={{
              marginRight: "23px",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <div style={{ width: `${checkedPercent}%` }}></div>
            <div
              style={{
                width: 0,
                overflow: "visible",
              }}
            >
              <Pointer />
            </div>
          </div>
        </div>
        <ColorBar />
      </Axis>
      <Poor>{t("result.poor")}</Poor>
      <Good>{t("result.good")}</Good>
    </Base>
  )
}
